import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import Select from "components/common/Select";
import { useCardDraft } from "../common/useCardDraft";
import ActivityService from "./ActivityService";
import ActivityCatalogAutocomplete from "./ActivityCatalogAutocomplete";
import {
  ACTIVITY_STATUS,
  ACTIVITY_INTENSITY,
  activityIntensityBand,
  activityStatusBand,
  MAX_DESCRIPTION_WORDS,
  countWords,
  clampWords,
} from "./data";
import { ACT_V2_REFRESH } from "./events";

/**
 * Dual-purpose write sheet (Add / Edit) for one Activity row — px-matched to
 * Figma 3071:4236 (`Editor · Filled (Activity hero)`, 340 wide).
 *
 * This was ActivityModal: a centred overlay on a scrim. The board draws it as
 * a bordered CARD in the third column with no backdrop, which is the rail
 * idiom the other v3 sections use, so it now lives in the rail driven by the
 * shared useSheetStack — several sheets can be open at once and are saved one
 * at a time.
 *
 * `editing` present = Edit mode (POST /:patientId/:activityId, a full-detail
 * replace); absent = Add (POST /:patientId).
 *
 * The Activity picker is a debounced fuzzy-search against the shared catalog
 * on the same service; free-text is allowed if the user types a name that
 * isn't in the catalog (catalogId/category submit empty).
 */

/* Board 3071:4256 / 4303 — the numeric fields are 58x42 boxes with a 22px
   arrow rail split by its own 1px rules, and 11px stroked chevrons. Both
   boards author them, so they are a spec rather than one board's drift.

   The arrows only ever set the SAME state the input already sets, clamped to
   the same bounds the input already declares — no new validation path. Empty
   coerces to 0 on the first click so the value can never go NaN. */
const StepChevron = ({ up }) => (
  <svg viewBox="0 0 11 11" width="11" height="11" fill="none" aria-hidden="true">
    <path
      d={up ? "M2 7.5L5.5 3.5L9 7.5" : "M1.5 3.5L5.5 7.5L9.5 3.5"}
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
StepChevron.propTypes = { up: PropTypes.bool };

/* Board 3071:4311 `icon/chev-up-16` — 11x11 of chevron in a 16 box, stroke
   #6a7282 at 1.5, round caps. A chevron-up in this design system is the
   card-collapse affordance (cf. .ad3-dlg-chev, .dx-card-chevron), not a
   dismiss — Cancel in the footer is what closes the sheet. It rotates to
   point down when collapsed. */
const CollapseChevron = () => (
  <svg viewBox="0 0 11 11" width="16" height="16" fill="none" aria-hidden="true">
    <path
      d="M2 7.5L5.5 3.5L9 7.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const NumberStepper = ({ id, value, onChange, min, max, label, invalid, disabled }) => {
  const bump = (delta) => {
    const n = parseInt(value, 10);
    // Empty state → bump-up lands on `min`, bump-down stays empty.
    // Otherwise clamp the increment to [min, max].
    if (!Number.isFinite(n)) {
      onChange(delta > 0 ? String(min) : "");
      return;
    }
    onChange(String(Math.max(min, Math.min(max, n + delta))));
  };
  const onType = (e) => {
    const raw = e.target.value;
    if (raw === "") { onChange(""); return; }
    const digits = raw.replace(/\D/g, "");
    if (digits === "") { onChange(""); return; }
    const n = parseInt(digits, 10);
    if (!Number.isFinite(n)) { onChange(""); return; }
    onChange(String(Math.max(min, Math.min(max, n))));
  };
  return (
    <div className={`pa3-step${invalid ? " is-error" : ""}`}>
      <input
        id={id}
        className="pa3-step-input"
        type="number"
        min={min}
        max={max}
        step="1"
        value={value}
        onChange={onType}
        placeholder="-"
        disabled={disabled}
        aria-invalid={!!invalid}
      />
      <div className="pa3-step-arrows">
        <button type="button" onClick={() => bump(+1)} disabled={disabled}
          aria-label={`Increase ${label}`} tabIndex={-1}>
          <StepChevron up />
        </button>
        <button type="button" onClick={() => bump(-1)} disabled={disabled}
          aria-label={`Decrease ${label}`} tabIndex={-1}>
          <StepChevron />
        </button>
      </div>
    </div>
  );
};
NumberStepper.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  min: PropTypes.number,
  max: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  invalid: PropTypes.bool,
  disabled: PropTypes.bool,
};
NumberStepper.defaultProps = {
  min: 0,
};

const ActivityForm = ({
  patientId,
  formId,
  editing,
  existingList,
  ordinal,
  showOrdinal,
  blockedBySibling,
  onFilledChange,
  onSavingChange,
  onClaimChange,
  siblingClaims,
  onClose,
  // Transcript-mode extensions — all optional; unset preserves standalone.
  initialPayload,
  onSave,
  onReject,
  extraActiveKeys,
  saving,
  error,
  themeClass,
  lockName,
  isReopen,
  menuClassName,
}) => {
  const activity = editing;
  const isEdit = !!activity;
  const p = initialPayload || null;
  const isTranscript = typeof onSave === "function";
  const lockActivityName = !!lockName;

  // ZY-10356 Phase 1 — transcript-mode draft preservation. No-op in
  // standalone.
  const [draft, saveDraft] = useCardDraft(formId);

  const [name, setName] = useState(() => (
    draft && "name" in draft
      ? draft.name
      : ((activity && activity.name) || (p && (p.name || p.value)) || "")
  ));
  const [category, setCategory] = useState(() => (
    draft && "category" in draft
      ? draft.category
      : ((activity && activity.category) || (p && p.category) || "")
  ));
  const [catalogId, setCatalogId] = useState(() => (
    draft && "catalogId" in draft
      ? draft.catalogId
      : ((activity && activity.catalogId) || (p && (p.catalog_id || p.catalogId)) || "")
  ));
  // Start blank in Add mode so the clinician must enter real values — safer
  // than pre-filling defaults that could get saved by an accidental submit.
  // Edit mode prefills with the saved values as before. Transcript-mode
  // AI seeds also prefill, since a captured payload is a proposed value
  // the reviewer will confirm.
  const [daysPerWeek, setDaysPerWeek] = useState(() => {
    if (draft && "daysPerWeek" in draft) return draft.daysPerWeek;
    if (activity && activity.daysPerWeek) return activity.daysPerWeek;
    if (p && (p.days_per_week != null || p.daysPerWeek != null)) {
      return String(p.days_per_week != null ? p.days_per_week : p.daysPerWeek);
    }
    return "";
  });
  const [minutesPerSession, setMinutesPerSession] = useState(() => {
    if (draft && "minutesPerSession" in draft) return draft.minutesPerSession;
    if (activity && activity.minutesPerSession) return activity.minutesPerSession;
    if (p && (p.minutes_per_session != null || p.minutesPerSession != null)) {
      return String(p.minutes_per_session != null ? p.minutes_per_session : p.minutesPerSession);
    }
    return "";
  });
  const [intensity, setIntensity] = useState(() => (
    draft && "intensity" in draft
      ? draft.intensity
      : ((activity && activity.intensity) || (p && p.intensity) || "")
  ));
  const [status, setStatus] = useState(() => (
    draft && "status" in draft
      ? draft.status
      : ((activity && activity.status) || (p && p.status) || "active")
  ));
  const [description, setDescription] = useState(() => (
    draft && "description" in draft
      ? draft.description
      : ((activity && activity.description) || (p && p.description) || "")
  ));
  const [submitting, setSubmitting] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // ZY-10356 Phase 1 — sync persistent fields to the modal-root draft
  // store on every change.
  useEffect(() => {
    saveDraft({
      name, category, catalogId, daysPerWeek, minutesPerSession,
      intensity, status, description,
    });
  }, [
    saveDraft,
    name, category, catalogId, daysPerWeek, minutesPerSession,
    intensity, status, description,
  ]);
  // Transcript wrappers don't pass `existingList` (they don't own the
  // doc), so the duplicate-name check would silently no-op. Fetch a
  // fresh list on mount so the standalone rule keeps biting in
  // transcript mode too. Standalone path ignores this state — the
  // parent-provided prop wins.
  const [fetchedExistingList, setFetchedExistingList] = useState(null);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => {
    if (existingList != null) return; // parent already provides it.
    if (!patientId) return;
    const svc = new ActivityService();
    svc.list(
      patientId,
      (doc) => {
        if (!mounted.current) return;
        setFetchedExistingList(
          (doc && Array.isArray(doc.activities)) ? doc.activities : []
        );
      },
      () => {
        if (!mounted.current) return;
        setFetchedExistingList([]);
      }
    );
  }, [patientId, existingList]);

  // Effective list — prop wins so standalone callers stay in charge; a
  // transcript-mode mount falls back to the JIT fetch above.
  const effectiveExistingList =
    existingList != null ? existingList : (fetchedExistingList || []);

  // Set of lowercased names of activities already on the patient's log,
  // fed to the ActivityCatalogAutocomplete so already-added rows render
  // greyed with an "already added" pill. On an Edit sheet the row being
  // edited is excluded — otherwise it would flag itself.
  const activeActivityNames = useMemo(() => {
    const s = new Set();
    for (const a of effectiveExistingList) {
      if (!a) continue;
      if (isEdit && activity && a.activityId === activity.activityId) continue;
      const key = ((a.name || "").trim().toLowerCase());
      if (key) s.add(key);
    }
    return s;
  }, [effectiveExistingList, isEdit, activity]);
  const isActivityAlreadyActive = useCallback(
    (row) => {
      const key = ((row && row.name) || "").trim().toLowerCase();
      if (!key) return false;
      if (activeActivityNames.has(key)) return true;
      // ZY-10312 — transcript mode passes in-call captures via
      // extraActiveKeys so the dropdown flags activities staged *this
      // call* the same way DB-persisted actives are flagged. Keys
      // lowercased upstream in CapturedSections.
      return !!extraActiveKeys && extraActiveKeys.has(key);
    },
    [activeActivityNames, extraActiveKeys],
  );

  const close = useCallback(() => onClose(formId), [onClose, formId]);

  const handleCatalogPick = useCallback(({ name: n, catalogId: cid, category: cat }) => {
    setName(n);
    setCatalogId(cid);
    setCategory(cat);
  }, []);

  // Block adding the same activity twice. Compare by catalogId when we have
  // one, otherwise fall back to a case-insensitive name match. Edit skips
  // its own row so re-saving the current entry never self-blocks.
  /* A sibling ADD sheet can be claiming this same name and the server list
     cannot see it — it has not written yet. */
  const siblingClaim =
    !isEdit &&
    !!name.trim() &&
    (siblingClaims || []).indexOf(name.trim().toLowerCase()) >= 0;
  const duplicateActivityError = useMemo(() => {
    // ZY-10312 — skip the clash check when the identity is already
    // locked. This covers:
    //   • Standalone edit (isEdit): user is updating an existing row;
    //     its own name is in the list and would otherwise self-flag.
    //   • Transcript reedit (lockActivityName): the staged row IS the
    //     one in question. Flagging it would (a) show a spurious
    //     "already in list" error on simple re-open and (b) disable
    //     Add — preventing a reviewer from reviving a rejected row.
    if (isEdit || lockActivityName) return "";
    const key = (name || "").trim().toLowerCase();
    if (!key) return "";
    const clash = (effectiveExistingList || []).some((a) => {
      if (catalogId && a.catalogId) return a.catalogId === catalogId;
      return (a.name || "").trim().toLowerCase() === key;
    });
    return clash ? `"${name.trim()}" is already in the activity list.` : "";
  }, [name, catalogId, effectiveExistingList, isEdit, lockActivityName]);

  // Add mode requires a real catalog pick — free-typed names are rejected so
  // downstream analytics stays keyed off known catalog rows.
  const catalogPicked = !!catalogId;

  const days = Number(daysPerWeek) || 0;
  const mins = Number(minutesPerSession) || 0;

  // Hard-range validation for the two integer inputs — anything outside
  // [1, 7] / [1, 1440] blocks Save with an inline error. Zero is
  // deliberately rejected (an activity with 0 days per week or 0
  // minutes per session isn't a real activity — the reviewer should
  // pick Status="inactive" instead, or omit the row). Empty stays a
  // separate "not filled in yet" state.
  const DAYS_MIN = 1;
  const DAYS_MAX = 7;
  const MINS_MIN = 1;
  const MINS_MAX = 1440;
  const daysRaw = String(daysPerWeek).trim();
  const minsRaw = String(minutesPerSession).trim();
  const daysError = (() => {
    if (daysRaw === "") return "";
    const n = Number(daysRaw);
    if (!Number.isFinite(n) || !Number.isInteger(n)) return "Days per week must be a whole number.";
    if (n < DAYS_MIN || n > DAYS_MAX) return `Days per week must be between ${DAYS_MIN} and ${DAYS_MAX}.`;
    return "";
  })();
  const minsError = (() => {
    if (minsRaw === "") return "";
    const n = Number(minsRaw);
    if (!Number.isFinite(n) || !Number.isInteger(n)) return "Minutes per session must be a whole number.";
    if (n < MINS_MIN || n > MINS_MAX) return `Minutes per session must be between ${MINS_MIN} and ${MINS_MAX}.`;
    return "";
  })();
  const daysFilled = daysRaw !== "";
  const minsFilled = minsRaw !== "";
  // Only a CHOSEN value gets a dot — the placeholder state has none.
  const intensityBand = intensity ? activityIntensityBand(intensity) : null;
  const statusBand = status ? activityStatusBand(status) : null;

  // Edit-mode dirty check — save is a no-op unless something actually
  // changed. Compare each editable field against the initial activity so
  // hitting Save without touching anything is disabled (prevents duplicate
  // audit rows and pointless network calls).
  const isDirty = useMemo(() => {
    if (!isEdit) return true;
    if (days !== (activity.daysPerWeek || 0)) return true;
    if (mins !== (activity.minutesPerSession || 0)) return true;
    if (intensity !== (activity.intensity || "")) return true;
    if (status !== (activity.status || "active")) return true;
    if (description.trim() !== (activity.description || "").trim()) return true;
    return false;
  }, [isEdit, activity, days, mins, intensity, status, description]);

  // The rail's blank-sheet guard: an add sheet counts as touched once a name
  // is on it.
  useEffect(() => {
    if (typeof onFilledChange !== "function") return;
    onFilledChange(formId, isEdit || !!name.trim());
  }, [formId, isEdit, name, onFilledChange]);

  // Cross-sheet uniqueness: two open ADD sheets both claiming "Walking" would
  // each pass the server-list check and both save, because neither list can
  // see a sibling that has not written yet.
  useEffect(() => {
    if (typeof onClaimChange !== "function") return;
    onClaimChange(formId, isEdit ? null : name.trim().toLowerCase());
  }, [formId, isEdit, name, onClaimChange]);

  const busy = submitting || !!saving;
  // Every standalone rule applies in transcript mode too — same catalog
  // pick, duplicate check, integer bounds and intensity requirement. The
  // only accommodation is `lockActivityName` (transcript click-to-edit
  // re-open freezes the identity, so the catalog-pick check is skipped
  // the same way Edit mode skips it — the row's name/catalogId came
  // from the captured payload, not a fresh user pick).
  const canSubmit =
    !busy &&
    !blockedBySibling &&
    !!name.trim() &&
    !duplicateActivityError &&
    !siblingClaim &&
    (isEdit || lockActivityName || catalogPicked) &&
    daysFilled &&
    minsFilled &&
    // Intensity no longer carries a default, so it has to be chosen.
    !!intensity &&
    !daysError &&
    !minsError &&
    isDirty;

  const submit = () => {
    if (!canSubmit) return;
    if (!isTranscript && !patientId) return;
    setFeedback(null);
    const body = {
      name: name.trim(),
      category,
      catalogId,
      daysPerWeek: Math.max(0, Math.min(DAYS_MAX, parseInt(daysPerWeek, 10) || 0)),
      minutesPerSession: Math.max(0, Math.min(MINS_MAX, parseInt(minutesPerSession, 10) || 0)),
      intensity,
      status,
      description: description.trim(),
    };

    // Transcript mode — hand out; caller owns persistence. Skip the
    // local submitting/feedback + ACT_V2_REFRESH emit.
    if (isTranscript) {
      onSave(body);
      return;
    }

    setSubmitting(true);
    if (typeof onSavingChange === "function") {
      onSavingChange(formId, true);
    }
    const svc = new ActivityService();
    const done = () => {
      EventService.emit(ACT_V2_REFRESH);
      if (!mounted.current) return;
      if (typeof onSavingChange === "function") {
        onSavingChange(formId, false);
      }
      // The rail drops this sheet, which unmounts us — anything set after
      // this point would be a setState on an unmounted component.
      close();
    };
    const failed = (err) => {
      // Unmounted means the rail already dropped this sheet and closeSheet
      // cleared the saving flag on the way out.
      if (!mounted.current) return;
      if (typeof onSavingChange === "function") {
        onSavingChange(formId, false);
      }
      setSubmitting(false);
      setFeedback({
        kind: "err",
        text: (err && err.message) || "Save failed — please retry",
      });
    };
    // Fresh version per mutation, never the one this sheet mounted with.
    // Siblings are blocked from saving while this is in flight, so the value
    // read here cannot go stale between the read and the write.
    svc.list(
      patientId,
      (doc) => {
        const withVersion = { ...body, expectedVersion: (doc && doc.version) || 0 };
        if (isEdit) svc.update(patientId, activity.activityId, withVersion, done, failed);
        else svc.add(patientId, withVersion, done, failed);
      },
      failed
    );
  };

  const foldId = `act-fold-${formId}`;
  const daysId = `act-days-${formId}`;
  const minsId = `act-mins-${formId}`;
  const descId = `act-desc-${formId}`;

  const rootClass = [
    "pa3-dlg",
    collapsed ? "is-collapsed" : "",
    themeClass || "",
  ].filter(Boolean).join(" ");
  const displayError = feedback && feedback.kind === "err" ? feedback.text : error || "";

  return (
    <section className={rootClass}>
      {/* ZY-10356 Phase 6 Rule 1 — top-right X close only on re-opens
          (#2/#4/#6/#8). First-opens (#1/#3/#5/#7) leave the footer
          Reject as the sole dismiss affordance. Mirrors Alg/CC/Med. */}
      {isTranscript && isReopen ? (
        <button
          type="button"
          className="zy-card-x"
          onClick={close}
          aria-label="Close"
        >
          &times;
        </button>
      ) : null}
      <div>
        {/* Title/subtitle row removed for every mode — the sheet lands
            directly on the form fields. Ordinal (stacked-sheets marker)
            is still surfaced when the caller asked for it, so the rail
            can label sibling sheets in a stack. */}
        {showOrdinal && (
          <header className="cc-modal-head">
            <span className="zy-ord" aria-hidden="true">{ordinal}</span>
          </header>
        )}

        {/* Boards 3071:4303 / 4370 draw the `Activity` label row full-bleed
            with a chevron-up at its right edge, which makes it the card's
            collapse handle. It therefore sits ABOVE the fold — inside it the
            control would disappear on the first click with no way back.
            The `· fuzzy search` hint is Regular 11/14 #6a7282 on an 8px gap,
            and only applies while the field IS a search box; once the
            identity is locked (Edit, or a catalog pick) there is nothing
            left to search. */}
        <div className="pa3-lbl-row pa3-dlg-handle">
          <span className="sec-label">
            Activity <span className="sec-label-required">*</span>
            {!isEdit && !catalogPicked && !lockActivityName && (
              <span className="pa3-lbl-hint">· fuzzy search</span>
            )}
          </span>
          <button
            type="button"
            className="pa3-dlg-chev"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            aria-controls={foldId}
            aria-label={collapsed ? "Expand activity form" : "Collapse activity form"}
          >
            <CollapseChevron />
          </button>
        </div>

        <div className="pa3-dlg-fold" id={foldId}>
        <div className="pa3-dlg-fold-inner">
        <div className="cc-modal-body">
          <div className="sec-field">
            {isEdit || lockActivityName ? (
              // Locked identity — same pattern as Allergies v2 Edit mode.
              // The activity name is captured at Add-time and never editable
              // afterwards (status / days / minutes / intensity are).
              // Transcript re-edit (lockActivityName=true) also locks so
              // the identity can't drift between review and save.
              <div className="alg-locked">
                <span className="alg-locked-name">{name}</span>
                {category && (
                  <span className="alg-locked-tag">{category}</span>
                )}
              </div>
            ) : catalogPicked ? (
              <div className="alg-locked">
                <span className="alg-locked-name">{name}</span>
                <span className="alg-locked-tail">
                  {category && (
                    <span className="alg-locked-tag">{category}</span>
                  )}
                  <button
                    type="button"
                    className="alg-locked-clear"
                    onClick={() =>
                      handleCatalogPick({ name: "", catalogId: "", category: "" })
                    }
                    disabled={busy}
                    aria-label="Change activity"
                    title="Change activity"
                  >
                    ×
                  </button>
                </span>
              </div>
            ) : (
              <ActivityCatalogAutocomplete
                value={name}
                catalogId={catalogId}
                onChange={handleCatalogPick}
                disabled={busy}
                isAlreadyActive={isActivityAlreadyActive}
              />
            )}
          </div>

          {duplicateActivityError && (
            <div className="fam-warn is-error">{duplicateActivityError}</div>
          )}

          <div className="act-num-row">
            <div className="sec-field">
              <label className="sec-label" htmlFor={daysId}>
                Days per week <span className="sec-label-required">*</span>
              </label>
              <NumberStepper
                id={daysId}
                value={daysPerWeek}
                onChange={setDaysPerWeek}
                min={DAYS_MIN}
                max={DAYS_MAX}
                label="days per week"
                invalid={!!daysError}
                disabled={busy}
              />
              {daysError && <p className="fam-field-error">{daysError}</p>}
            </div>
            <div className="sec-field">
              <label className="sec-label" htmlFor={minsId}>
                Minutes per session <span className="sec-label-required">*</span>
              </label>
              <NumberStepper
                id={minsId}
                value={minutesPerSession}
                onChange={setMinutesPerSession}
                min={MINS_MIN}
                max={MINS_MAX}
                label="minutes per session"
                invalid={!!minsError}
                disabled={busy}
              />
              {minsError && <p className="fam-field-error">{minsError}</p>}
            </div>
          </div>

          {!daysError && !minsError && (!daysFilled || !minsFilled) && (
            <p className="act-num-hint">
              Enter Days per week ({DAYS_MIN}–{DAYS_MAX}) and Minutes per session
              ({MINS_MIN}–{MINS_MAX}) to enable Save.
            </p>
          )}

          {/* Board 3071:4419 / 4427 lead a CHOSEN value with a 7px dot. The
              board draws a flat #29a873 on both of the two states it happens
              to show, which is sample drift — this section's own rule is that
              the dot carries the label's own ink (see data.js), so it is
              driven from the band and generalises to Vigorous / Inactive.
              Select owns its own DOM, so the custom property rides on the
              field wrapper and the dot is a ::before on the trigger — the
              same mechanism the addictions editor uses. */}
          <div
            className={`sec-field pa3-fld pa3-fld-intensity${intensityBand ? " has-dot" : ""}`}
            style={intensityBand ? { "--pa3-dot": intensityBand.dot } : undefined}
          >
            <label className="sec-label">
              Intensity <span className="sec-label-required">*</span>
            </label>
            <Select
              value={intensity}
              options={ACTIVITY_INTENSITY}
              getKey={(o) => o.key}
              getLabel={(o) => o.label}
              placeholder="Select intensity"
              onChange={setIntensity}
              menuClassName={menuClassName || ""}
            />
          </div>

          <div
            className={`sec-field pa3-fld${statusBand ? " has-dot" : ""}`}
            style={statusBand ? { "--pa3-dot": statusBand.dot } : undefined}
          >
            <label className="sec-label">
              Status <span className="sec-label-required">*</span>
            </label>
            <Select
              value={status}
              options={ACTIVITY_STATUS}
              getKey={(o) => o.key}
              getLabel={(o) => o.label}
              onChange={setStatus}
              menuClassName={menuClassName || ""}
            />
          </div>

          <div className="sec-field">
            <label className="sec-label" htmlFor={descId}>
              Description <span className="sec-label-note">optional</span>
              <span className="dx-word-hint">
                {countWords(description)} / {MAX_DESCRIPTION_WORDS} words
              </span>
            </label>
            {/* Placeholder copy per board 3071:4303 (node 4090:15095). */}
            <textarea
              className="sec-textarea"
              rows={3}
              value={description}
              onChange={(e) =>
                setDescription(clampWords(e.target.value, MAX_DESCRIPTION_WORDS))
              }
              placeholder="Add a short note…"
              disabled={busy}
            />
          </div>

          {displayError && <div className="cc-feedback is-err">{displayError}</div>}
        </div>

        <footer className="cc-modal-foot">
          {/* Never disabled while saving: a write that never settles would
              otherwise pin the sheet open with no way out. */}
          <button
            type="button"
            className="sec-btn sec-btn-ghost"
            onClick={() => {
              if (isTranscript && !busy && typeof onReject === "function") {
                onReject();
                return;
              }
              close();
            }}
          >
            {busy ? "Close" : (isTranscript ? "Reject" : "Cancel")}
          </button>
          <button
            type="button"
            className={`sec-btn sec-btn-primary${themeClass ? ` ${themeClass}` : ""}`}
            onClick={submit}
            disabled={!canSubmit}
            title={blockedBySibling ? "Another activity is saving" : undefined}
          >
            {/* Labels follow CC's convention (see ComplaintForm.js) so
                the 8-case card contract reads consistently across
                sections — isEdit (#5-#8: DB-update first-open / reopen)
                says "Update"; new-entry cards (#1-#4) say "Add" in
                transcript and "Save activity" in standalone. */}
            {busy
              ? (isEdit ? "Updating…" : (isTranscript ? "Adding…" : "Saving…"))
              : isEdit
              ? "Update"
              : isTranscript
              ? "Add"
              : "Save activity"}
          </button>
        </footer>
        </div>
        </div>
      </div>
    </section>
  );
};

ActivityForm.propTypes = {
  patientId: PropTypes.number,
  formId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /** undefined = Add; the record = Edit. */
  editing: PropTypes.object,
  /** Current table rows — the server-side half of the duplicate check. */
  existingList: PropTypes.array,
  ordinal: PropTypes.number,
  showOrdinal: PropTypes.bool,
  blockedBySibling: PropTypes.bool,
  onFilledChange: PropTypes.func,
  onSavingChange: PropTypes.func,
  onClaimChange: PropTypes.func,
  siblingClaims: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  // Transcript-mode extensions — all optional; unset preserves standalone.
  initialPayload: PropTypes.object,
  onSave: PropTypes.func,
  onReject: PropTypes.func,
  /** ZY-10312 — transcript-mode set of lowercased activity names that
   *  have already been staged THIS call. Merged into the "already
   *  added" predicate so the search dropdown flags in-call duplicates
   *  the same way DB-persisted actives are flagged. Standalone callers
   *  leave this null. */
  extraActiveKeys: PropTypes.instanceOf(Set),
  saving: PropTypes.bool,
  error: PropTypes.string,
  themeClass: PropTypes.string,
  /**
   * Lock the activity name (+ category tag) read-only. Transcript
   * click-to-edit sets this so the identity can't drift between review
   * and save.
   */
  lockName: PropTypes.bool,
  /** ZY-10356 Phase 6 — surfaces the top-right X-close affordance on
   *  reopen cards (#2/#4/#6/#8). First-opens keep footer Reject only. */
  isReopen: PropTypes.bool,
  /** Extra class for the Intensity + Status dropdown portals. */
  menuClassName: PropTypes.string,
};

ActivityForm.defaultProps = {
  initialPayload: null,
  onSave: null,
  extraActiveKeys: null,
  saving: false,
  error: "",
  themeClass: "",
  lockName: false,
  isReopen: false,
  menuClassName: "",
};

export default ActivityForm;
